import { useForm, router, usePage } from '@inertiajs/react'
import AccountLayout from '@/layouts/AccountLayout'
import type { Member, Invitation, SharedProps } from '@/types'
import type { FormEvent } from 'react'
import { useRef, useState } from 'react'
import { useAccountPath } from '@/lib/useAccountPath'

interface TeamProps {
  members: Member[]
  invitations: Invitation[]
  errors?: Record<string, string>
}

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
}

const ROLE_BADGE_COLOR: Record<string, string> = {
  owner: 'badge-primary',
  admin: 'badge-info',
  member: 'badge-ghost',
}

type MemberRole = 'owner' | 'admin' | 'member'

export default function Team({ members, invitations, errors = {} }: TeamProps) {
  const { current_membership } = usePage<{ props: SharedProps & TeamProps }>().props as unknown as SharedProps & TeamProps
  const ap = useAccountPath()
  const inviteDialogRef = useRef<HTMLDialogElement>(null)
  const editDialogRef = useRef<HTMLDialogElement>(null)
  const isOwner = current_membership?.owner ?? false

  const inviteForm = useForm({
    email_address: '',
    roles: ['member'] as Array<'admin' | 'member'>,
  })

  const [editingMember, setEditingMember] = useState<Member | null>(null)
  const [editRoles, setEditRoles] = useState<MemberRole[]>([])
  const [editProcessing, setEditProcessing] = useState(false)

  function openInvite() {
    inviteForm.reset()
    inviteDialogRef.current?.showModal()
  }

  function toggleInviteRole(role: 'admin' | 'member') {
    const next = inviteForm.data.roles.includes(role)
      ? inviteForm.data.roles.filter((r) => r !== role)
      : [...inviteForm.data.roles, role]
    inviteForm.setData('roles', next)
  }

  function handleInviteSubmit(e: FormEvent) {
    e.preventDefault()
    inviteForm.post(ap('/team/invitations'), {
      onSuccess: () => {
        inviteDialogRef.current?.close()
      },
    })
  }

  function openEditRoles(member: Member) {
    setEditingMember(member)
    setEditRoles([...member.roles])
    editDialogRef.current?.showModal()
  }

  function toggleEditRole(role: MemberRole) {
    setEditRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    )
  }

  function handleEditRolesSubmit(e: FormEvent) {
    e.preventDefault()
    if (!editingMember || editRoles.length === 0) return
    router.patch(
      ap(`/team/memberships/${editingMember.id}`),
      { roles: editRoles },
      {
        onStart: () => setEditProcessing(true),
        onFinish: () => setEditProcessing(false),
        onSuccess: () => {
          editDialogRef.current?.close()
        },
      }
    )
  }

  function handleRemoveMember(member: Member) {
    if (!confirm(`Remove ${member.email_address} from the team?`)) return
    router.delete(ap(`/team/memberships/${member.id}`))
  }

  function handleResendInvitation(invitation: Invitation) {
    router.post(ap(`/team/invitations/${invitation.id}/resend`))
  }

  function handleCancelInvitation(invitation: Invitation) {
    if (!confirm(`Cancel the invitation to ${invitation.email_address}?`)) return
    router.delete(ap(`/team/invitations/${invitation.id}`))
  }

  function canChangeRole(member: Member): boolean {
    if (member.is_self && member.last_owner) return false
    if (!isOwner && member.roles.includes('owner')) return false
    return true
  }

  function canRemove(member: Member): boolean {
    if (member.is_self) return false
    if (member.last_owner) return false
    if (!isOwner && member.roles.includes('owner')) return false
    return true
  }

  return (
    <AccountLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Team</h1>
            <p className="text-sm text-base-content/60 mt-1">
              Invite teammates and manage their access.
            </p>
          </div>
          <button className="btn btn-primary btn-sm" onClick={openInvite}>
            <span className="iconify lucide--plus size-4" />
            Invite teammate
          </button>
        </div>

        <div className="card bg-base-100">
          <div className="card-body">
            <h2 className="card-title text-lg">Members</h2>

            <div className="overflow-x-auto mt-2">
              <table className="table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Joined</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.id}>
                      <td>
                        {m.email_address}
                        {m.is_self && <span className="badge badge-soft badge-ghost badge-sm ms-2">You</span>}
                      </td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {m.roles.map((role) => (
                            <span key={role} className={`badge ${ROLE_BADGE_COLOR[role]} badge-sm capitalize`}>
                              {ROLE_LABELS[role]}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="text-sm text-base-content/60">
                        {new Date(m.joined_at).toLocaleDateString()}
                      </td>
                      <td className="text-end">
                        {canChangeRole(m) || canRemove(m) ? (
                          <div className="dropdown dropdown-end">
                            <div tabIndex={0} role="button" className="btn btn-square btn-ghost btn-sm" title="Actions">
                              <span className="iconify lucide--ellipsis-vertical size-4" />
                            </div>
                            <ul tabIndex={0} className="dropdown-content menu bg-base-100 rounded-box z-10 w-48 p-2 shadow-sm border border-base-300">
                              {canChangeRole(m) && (
                                <li><button onClick={() => openEditRoles(m)}>Edit roles</button></li>
                              )}
                              {canRemove(m) && (
                                <>
                                  <li><div className="divider my-0" /></li>
                                  <li><button className="text-error" onClick={() => handleRemoveMember(m)}>Remove</button></li>
                                </>
                              )}
                            </ul>
                          </div>
                        ) : (
                          <span className="text-base-content/30 text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="card bg-base-100">
          <div className="card-body">
            <h2 className="card-title text-lg">Pending invitations</h2>

            {invitations.length === 0 ? (
              <p className="text-sm text-base-content/50 mt-2">No pending invitations.</p>
            ) : (
              <div className="overflow-x-auto mt-2">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Sent</th>
                      <th>Expires</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invitations.map((inv) => (
                      <tr key={inv.id}>
                        <td>{inv.email_address}</td>
                        <td>
                          <div className="flex flex-wrap gap-1">
                            {inv.roles.map((role) => (
                              <span key={role} className={`badge ${ROLE_BADGE_COLOR[role]} badge-sm capitalize`}>
                                {ROLE_LABELS[role]}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="text-sm text-base-content/60">
                          {new Date(inv.created_at).toLocaleDateString()}
                        </td>
                        <td className="text-sm">
                          {inv.expired ? (
                            <span className="badge badge-soft badge-error badge-sm">Expired</span>
                          ) : (
                            <span className="text-base-content/60">{new Date(inv.expires_at).toLocaleDateString()}</span>
                          )}
                        </td>
                        <td className="text-end">
                          <button
                            className="btn btn-square btn-ghost btn-sm"
                            title="Resend"
                            onClick={() => handleResendInvitation(inv)}
                          >
                            <span className="iconify lucide--send size-4" />
                          </button>
                          <button
                            className="btn btn-square btn-ghost btn-sm text-error"
                            title="Cancel"
                            onClick={() => handleCancelInvitation(inv)}
                          >
                            <span className="iconify lucide--x size-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <dialog ref={inviteDialogRef} className="modal">
        <div className="modal-box">
          <h3 className="font-bold text-lg">Invite teammate</h3>
          <p className="text-sm text-base-content/60 mt-1">
            They'll receive an email with a link to join.
          </p>

          <form onSubmit={handleInviteSubmit} className="space-y-4 mt-4">
            {errors.email_address && (
              <div role="alert" className="alert alert-soft alert-error text-sm">
                {errors.email_address}
              </div>
            )}

            <fieldset className="fieldset">
              <legend className="fieldset-legend">Email</legend>
              <input
                type="email"
                className="input w-full"
                value={inviteForm.data.email_address}
                onChange={(e) => inviteForm.setData('email_address', e.target.value)}
                required
                autoFocus
              />
            </fieldset>

            <fieldset className="fieldset">
              <legend className="fieldset-legend">Role</legend>
              <label className="label">
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={inviteForm.data.roles.includes('admin')}
                  onChange={() => toggleInviteRole('admin')}
                />
                Admin — full access including team management
              </label>
              <label className="label">
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={inviteForm.data.roles.includes('member')}
                  onChange={() => toggleInviteRole('member')}
                />
                Member — view-only access to surveys and results
              </label>
              {errors.roles && <p className="text-error text-sm mt-1">{errors.roles}</p>}
            </fieldset>

            <div className="modal-action">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => inviteDialogRef.current?.close()}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary btn-sm"
                disabled={inviteForm.processing || inviteForm.data.roles.length === 0}
              >
                {inviteForm.processing && <span className="loading loading-spinner loading-sm" />}
                Send invitation
              </button>
            </div>
          </form>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button>close</button>
        </form>
      </dialog>

      <dialog ref={editDialogRef} className="modal">
        <div className="modal-box">
          <h3 className="font-bold text-lg">Edit roles</h3>
          <p className="text-sm text-base-content/60 mt-1">
            {editingMember?.email_address}
          </p>

          <form onSubmit={handleEditRolesSubmit} className="space-y-4 mt-4">
            <fieldset className="fieldset">
              <legend className="fieldset-legend">Roles</legend>
              <label className="label">
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={editRoles.includes('owner')}
                  onChange={() => toggleEditRole('owner')}
                  disabled={
                    !isOwner ||
                    (editingMember?.active === false && !editRoles.includes('owner'))
                  }
                />
                Owner — full control including billing and ownership
              </label>
              {editingMember?.active === false && !editRoles.includes('owner') && (
                <p className="text-xs text-base-content/50 mt-1">
                  Inactive members can't be made owners.
                </p>
              )}
              <label className="label">
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={editRoles.includes('admin')}
                  onChange={() => toggleEditRole('admin')}
                />
                Admin — full access including team management
              </label>
              <label className="label">
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={editRoles.includes('member')}
                  onChange={() => toggleEditRole('member')}
                />
                Member — view-only access to surveys and results
              </label>
              {!isOwner && (
                <p className="text-xs text-base-content/50 mt-1">
                  Only owners can change owner roles.
                </p>
              )}
            </fieldset>

            <div className="modal-action">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => editDialogRef.current?.close()}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary btn-sm"
                disabled={editProcessing || editRoles.length === 0}
              >
                {editProcessing && <span className="loading loading-spinner loading-sm" />}
                Save roles
              </button>
            </div>
          </form>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button>close</button>
        </form>
      </dialog>
    </AccountLayout>
  )
}
